/**
 * Paint Schemes Section Functionality
 * Handles paint selection, total calculation, and add to cart
 */

class PaintSchemes {
  constructor() {
    this.init();
  }

  init() {
    this.applyPaintTextContrast();
    this.bindEvents();
  }

  applyPaintTextContrast() {
    const paintOptions = document.querySelectorAll('.paint-option');
    paintOptions.forEach(optionEl => {
      if (!(optionEl instanceof HTMLElement)) return;
      const colorBox = optionEl.querySelector('.paint-option__color-box');
      const detailsEl = optionEl.querySelector('.paint-option__details');
      if (!(colorBox instanceof HTMLElement) || !(detailsEl instanceof HTMLElement)) return;

      const bgColor = optionEl.dataset.paintColor || '#cccccc';
      const isLight = this.isLightColor(bgColor);

      if (isLight) {
        detailsEl.classList.remove('paint-option__details--light');
        detailsEl.style.color = '#2A3141';
      } else {
        detailsEl.classList.add('paint-option__details--light');
        detailsEl.style.color = '#FCF9F0';
      }
    });
  }

  bindEvents() {
    // Select All buttons
    document.querySelectorAll('.paint-scheme-select-all').forEach(button => {
      button.addEventListener('click', this.handleSelectAll.bind(this));
    });

    // Individual paint checkboxes
    document.querySelectorAll('.paint-option__checkbox-input').forEach(checkbox => {
      checkbox.addEventListener('change', this.handleCheckboxChange.bind(this));
    });

    // Add to Cart buttons
    document.querySelectorAll('.paint-scheme-add-to-cart').forEach(button => {
      button.addEventListener('click', this.handleAddToCart.bind(this));
    });
  }

  handleSelectAll(event) {
    const button = event.currentTarget;
    const schemeIndex = button.dataset.schemeIndex;
    const card = document.querySelector(`.paint-scheme-card[data-scheme-index="${schemeIndex}"]`);
    const checkboxes = card.querySelectorAll('.paint-option__checkbox-input:not(:disabled)');
    
    // Check if all available are already selected
    const allChecked = Array.from(checkboxes).every(cb => cb.checked);
    
    // Toggle all available checkboxes
    checkboxes.forEach(checkbox => {
      checkbox.checked = !allChecked;
    });

    // Update total
    this.updateTotal(schemeIndex);
  }

  handleCheckboxChange(event) {
    const checkbox = event.target;
    const card = checkbox.closest('.paint-scheme-card');
    const schemeIndex = card.dataset.schemeIndex;
    
    this.updateTotal(schemeIndex);
  }

  /** @param {string} hex */
  isLightColor(hex) {
    const sanitized = hex.replace('#', '');
    if (sanitized.length !== 6) return true;
    const r = parseInt(sanitized.substring(0, 2), 16);
    const g = parseInt(sanitized.substring(2, 4), 16);
    const b = parseInt(sanitized.substring(4, 6), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.75;
  }

  updateTotal(schemeIndex) {
    const card = document.querySelector(`.paint-scheme-card[data-scheme-index="${schemeIndex}"]`);
    const checkboxes = card.querySelectorAll('.paint-option__checkbox-input:checked');
    const totalElement = card.querySelector('.paint-scheme-total__amount');
    const addToCartButton = card.querySelector('.paint-scheme-add-to-cart');
    
    let total = 0;
    checkboxes.forEach(checkbox => {
      const paintOption = checkbox.closest('.paint-option');
      const price = parseFloat(paintOption.dataset.price) || 0;
      total += price;
    });

    // Format price (assuming currency is in cents/pence)
    const formattedTotal = this.formatMoney(total);
    totalElement.textContent = formattedTotal;

    // Enable/disable add to cart button
    addToCartButton.disabled = checkboxes.length === 0;
  }

  formatMoney(cents) {
    // Get currency format from Shopify
    const money_format = window.Shopify?.currency?.active || 'GBP';
    const amount = (cents / 100).toFixed(2);
    
    // Simple format - adjust based on store's currency
    if (money_format === 'GBP' || document.documentElement.lang === 'en-GB') {
      return `£${amount}`;
    } else if (money_format === 'USD') {
      return `$${amount}`;
    } else if (money_format === 'EUR') {
      return `€${amount}`;
    }
    return `£${amount}`;
  }

  async handleAddToCart(event) {
    const button = event.currentTarget;
    const schemeIndex = button.dataset.schemeIndex;
    const card = document.querySelector(`.paint-scheme-card[data-scheme-index="${schemeIndex}"]`);
    const checkboxes = card.querySelectorAll('.paint-option__checkbox-input:checked');
    
    if (checkboxes.length === 0) return;

    // Check for unavailable paints first
    const unavailablePaints = [];
    checkboxes.forEach(checkbox => {
      const paintOption = checkbox.closest('.paint-option');
      const isAvailable = paintOption.dataset.available === 'true';
      if (!isAvailable) {
        unavailablePaints.push(paintOption.querySelector('.paint-option__title').textContent);
      }
    });

    if (unavailablePaints.length > 0) {
      // Show error for out of stock paints
      const paintNames = unavailablePaints.join(', ');
      button.querySelector('.custom-btn__text').textContent = `${unavailablePaints.length} PAINT${unavailablePaints.length > 1 ? 'S' : ''} OUT OF STOCK`;
      
      setTimeout(() => {
        button.querySelector('.custom-btn__text').textContent = 'ADD PAINTS TO BASKET';
        button.disabled = false;
      }, 3000);
      
      // Uncheck unavailable paints
      unavailablePaints.forEach(paintName => {
        const paintOption = Array.from(card.querySelectorAll('.paint-option')).find(
          option => option.querySelector('.paint-option__title').textContent === paintName
        );
        if (paintOption) {
          paintOption.querySelector('.paint-option__checkbox-input').checked = false;
        }
      });
      
      // Update total after removing unavailable paints
      this.updateTotal(schemeIndex);
      return;
    }

    // Disable button during request
    button.disabled = true;
    const originalText = button.querySelector('.custom-btn__text').textContent;
    button.querySelector('.custom-btn__text').textContent = 'ADDING...';

    // Build items array
    const items = Array.from(checkboxes).map(checkbox => {
      const paintOption = checkbox.closest('.paint-option');
      return {
        id: parseInt(paintOption.dataset.variantId),
        quantity: 1
      };
    });

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ items })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to add items to cart');
      }

      // Success - update button text temporarily
      button.querySelector('.custom-btn__text').textContent = 'ADDED!';
      
      // Clear checkboxes after successful addition
      checkboxes.forEach(checkbox => {
        checkbox.checked = false;
      });
      this.updateTotal(schemeIndex);
      
      // Refresh cart count if there's a cart counter on the page
      this.updateCartCount();

      // Reset button after delay
      setTimeout(() => {
        button.querySelector('.custom-btn__text').textContent = 'ADD PAINTS TO BASKET';
        button.disabled = false;
      }, 2000);

    } catch (error) {
      console.error('Error adding paints to cart:', error);
      
      // Show more specific error message
      let errorMessage = 'ERROR - TRY AGAIN';
      if (error.message.includes('sold out') || error.message.includes('inventory')) {
        errorMessage = 'SOME PAINTS NO LONGER AVAILABLE';
      }
      
      button.querySelector('.custom-btn__text').textContent = errorMessage;
      
      setTimeout(() => {
        button.querySelector('.custom-btn__text').textContent = 'ADD PAINTS TO BASKET';
        button.disabled = false;
      }, 3000);
    }
  }

  async updateCartCount() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      // Update cart count elements (common selectors)
      const cartCountElements = document.querySelectorAll('[data-cart-count], .cart-count, .cart-count-bubble span');
      cartCountElements.forEach(el => {
        el.textContent = cart.item_count;
      });

      // Dispatch custom event for other components
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
    } catch (error) {
      console.error('Error updating cart count:', error);
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new PaintSchemes());
} else {
  new PaintSchemes();
}
